import type { Booking, ClientPackage } from '@coachapp/shared';
import { Ticket, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useGetClientPackagesQuery,
  useUpdateBookingMutation,
  useUseClientPackageSessionMutation,
} from '../store/coachApi';
import Modal from './Modal';

export interface CompleteBookingRequest {
  bookingId: string;
  clientId: string;
  clientName?: string;
  updateData?: Partial<Booking>;
}

interface CompleteBookingPromptProps {
  request: CompleteBookingRequest | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const getAvailablePackages = (packages: ClientPackage[]) =>
  packages
    .filter((pkg) => pkg.status === 'ACTIVE' && pkg.usedSessions < pkg.totalSessions)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const CompleteBookingPrompt = ({ request, onClose, onSuccess }: CompleteBookingPromptProps) => {
  const { t } = useTranslation();
  const { data: packages = [], isFetching: packagesFetching } = useGetClientPackagesQuery(
    { clientId: request?.clientId ?? '' },
    { skip: !request },
  );
  const [updateBooking, { isLoading: updating }] = useUpdateBookingMutation();
  const [deductSession, { isLoading: deducting }] = useUseClientPackageSessionMutation();
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const availablePackages = useMemo(() => getAvailablePackages(packages), [packages]);
  const loading = updating || deducting;

  const requestKey = request?.bookingId ?? '';
  const [prevRequestKey, setPrevRequestKey] = useState(requestKey);
  if (requestKey !== prevRequestKey) {
    setPrevRequestKey(requestKey);
    setSelectedPackageId('');
    setError(null);
  }

  const defaultPackageId = availablePackages[0]?.id ?? '';
  const effectivePackageId =
    selectedPackageId && availablePackages.some((pkg) => pkg.id === selectedPackageId)
      ? selectedPackageId
      : defaultPackageId;

  if (!request) {
    return null;
  }

  const completeBooking = async (packageId?: string) => {
    setError(null);
    try {
      await updateBooking({
        id: request.bookingId,
        data: {
          status: 'COMPLETED',
          ...request.updateData,
        },
      }).unwrap();

      if (packageId) {
        await deductSession({ clientId: request.clientId, id: packageId }).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    }
  };

  const handleCompleteWithDeduct = () => {
    if (!effectivePackageId) {
      return;
    }
    void completeBooking(effectivePackageId);
  };

  return (
    <Modal onClose={onClose} panelClassName="sm:max-w-md">
      <div className="modal-header flex justify-between items-start gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            {t('Завершить тренировку?')}
          </h2>
          {request.clientName && (
            <p className="text-sm text-slate-500 mt-1">{request.clientName}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="btn-ghost shrink-0 -mr-1 -mt-1"
          aria-label={t('Отмена')}
        >
          <X size={22} />
        </button>
      </div>

      {packagesFetching ? (
        <div className="flex justify-center py-8">
          <div className="spinner h-8 w-8" />
        </div>
      ) : availablePackages.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t('Списать занятие из пакета?')}</p>
          <div>
            <label className="section-label mb-1 block">
              {t('Выберите пакет')}
            </label>
            <select
              className="input-field font-medium appearance-none cursor-pointer"
              value={effectivePackageId}
              onChange={(e) => setSelectedPackageId(e.target.value)}
              disabled={loading}
            >
              {availablePackages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name} ({pkg.usedSessions}/{pkg.totalSessions})
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
          <Ticket size={20} className="text-slate-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">{t('У клиента нет активных пакетов с доступными занятиями')}</p>
        </div>
      )}

      {error && (
        <p className="mt-4 text-sm text-red-600 font-medium">{error}</p>
      )}

      <div className="modal-actions flex flex-col-reverse sm:flex-row gap-3 mt-6">
        <button type="button" className="btn-secondary flex-1" onClick={onClose} disabled={loading}>
          {t('Отмена')}
        </button>
        {availablePackages.length > 0 ? (
          <>
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => void completeBooking()}
              disabled={loading}
            >
              {loading ? t('Сохранение...') : t('Только завершить')}
            </button>
            <button
              type="button"
              className="btn-primary flex-1 sm:flex-[1.2]"
              onClick={handleCompleteWithDeduct}
              disabled={loading || !effectivePackageId}
            >
              {loading ? t('Сохранение...') : t('Завершить и списать')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={() => void completeBooking()}
            disabled={loading}
          >
            {loading ? t('Сохранение...') : t('Завершить')}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default CompleteBookingPrompt;
